import React, { useCallback } from 'react';
import { Modal, Stack, styled, Box, Divider } from '@mui/material';
import { CustomTypography } from '../custom-typography';

interface BaseModalProps {
  open: boolean;
  title: string;
  description?: React.ReactNode;
  icon?: React.ReactNode;
  onClose?: () => void;
  children?: React.ReactNode;
  actions?: React.ReactNode;

  //tắt khi esc hoặc click ngoài
  closeOnBackdrop?: boolean;
  closeOnEsc?: boolean;

  maxWidth?: number;
  maxHeight?: string;
}

const StyledModal = styled(Stack)<{ maxWidth?: number, maxHeight?: string }>(
  ({ maxWidth = 320, maxHeight = '80vh'  }) => ({
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '100%',
    maxWidth,
    maxHeight,
    overflowY: 'auto',
    background: '#ffffff',
    padding: '1rem',
    borderRadius: '0.75rem',
  }
),
);

export const BaseModal = ({
  open,
  title,
  description,
  icon,
  onClose,
  children,
  actions,
  closeOnBackdrop = true,
  closeOnEsc = true,
  maxWidth,
}: BaseModalProps) => {
  const handleClose = useCallback(
    (_event: {}, reason: 'backdropClick' | 'escapeKeyDown') => {
      if (reason === 'backdropClick' && !closeOnBackdrop) return;
      if (reason === 'escapeKeyDown' && !closeOnEsc) return;

      onClose?.();
    },
    [onClose, closeOnBackdrop, closeOnEsc],
  );

  return (
    <Modal
      open={open}
      onClose={handleClose}
      disableEscapeKeyDown={!closeOnEsc}
      aria-labelledby="modal-title"
      aria-describedby="modal-description"
    >
      <StyledModal maxWidth={maxWidth}>
        <Box>
          {icon && <Box mb={1}>{icon}</Box>}

          <CustomTypography
            id="modal-title"
            value={title}
            fontWeight={500}
            fontSize="1rem"
            mb={1}
          />

          <Divider />

          <Stack gap={1} m="1rem 0">
            {description && (
              <CustomTypography
                id="modal-description"
                value={description}
                sx={{ textAlign: 'center', color: 'text.secondary' }}
              />
            )}

            {children}
          </Stack>

          {actions && (
            <>
              <Divider />
              <Stack
                direction="row"
                spacing={1}
                sx={{ width: '100%', justifyContent: 'end', mt: 1 }}
              >
                {actions}
              </Stack>
            </>
          )}
        </Box>
      </StyledModal>
    </Modal>
  );
};